import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../utils/api';
import {
  Rss,
  RefreshCw,
  Loader2,
  Twitter,
  Calendar,
  FileText,
  CheckCircle,
  AlertCircle,
  XCircle,
  Trash2,
  Filter,
  AlertTriangle
} from 'lucide-react';

interface RSSAppFeed {
  id: string;
  title: string;
  description: string;
  link: string;
  items_count: number;
  created_at: string;
  updated_at: string;
  last_post_date?: string | null;
  is_active?: boolean;
  error?: string;
  status?: 'healthy' | 'broken' | 'inactive';
}

type FilterType = 'all' | 'healthy' | 'broken' | 'inactive';

export function RSSAppFeeds() {
  const [feeds, setFeeds] = useState<RSSAppFeed[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [ingestingFeeds, setIngestingFeeds] = useState<Set<string>>(new Set());
  const [ingestingAll, setIngestingAll] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [configurationRequired, setConfigurationRequired] = useState(false);
  const [configurationError, setConfigurationError] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterType>('all');
  const [selectedFeeds, setSelectedFeeds] = useState<Set<string>>(new Set());
  const [deletingFeeds, setDeletingFeeds] = useState(false);
  const [brokenFeeds, setBrokenFeeds] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    loadFeeds();
  }, []);

  const loadFeeds = async () => {
    console.log('[DEBUG] 🏁 Entering loadFeeds');
    try {
      const data = await api.getRSSAppFeeds();
      console.log('[DEBUG] 📦 API Response Payload:', JSON.stringify(data, null, 2));

      if (data && Array.isArray(data.feeds)) {
        console.log(`[DEBUG] ✅ Setting feeds state with ${data.feeds.length} items`);

        // Enhance feeds with status information
        const enhancedFeeds = data.feeds.map((feed: RSSAppFeed) => {
          const daysSinceLastPost = feed.last_post_date
            ? (Date.now() - new Date(feed.last_post_date).getTime()) / (1000 * 60 * 60 * 24)
            : 999;

          let status: 'healthy' | 'broken' | 'inactive' = 'healthy';
          if (feed.error || brokenFeeds.has(feed.id)) {
            status = 'broken';
          } else if (daysSinceLastPost > 30 || !feed.last_post_date) {
            status = 'inactive';
          }

          return {
            ...feed,
            status,
            error: feed.error || brokenFeeds.get(feed.id)
          };
        });

        setFeeds(enhancedFeeds);
      } else {
        console.warn('[DEBUG] ⚠️ API returned non-array feeds. Resetting to empty array.');
        setFeeds([]);
      }

      // Handle configuration status
      if (data && (data as any).configurationRequired) {
        setConfigurationRequired(true);
        setConfigurationError((data as any).error || 'RSS.app API credentials not configured');
      } else {
        setConfigurationRequired(false);
        setConfigurationError(null);
      }

      // Check for recent errors from ingestion logs
      await checkBrokenFeeds();
    } catch (error) {
      console.error('[DEBUG] ❌ loadFeeds Error:', error);
      setMessage({ type: 'error', text: 'Failed to load RSS.app feeds' });
      setFeeds([]);
    } finally {
      console.log('[DEBUG] 🏁 Exiting loadFeeds');
      setLoading(false);
    }
  };

  const checkBrokenFeeds = async () => {
    try {
      const sources = await api.getMonitoringSources();
      if (sources?.rss_app?.last_sync?.errors) {
        const errors = sources.rss_app.last_sync.errors;
        const brokenMap = new Map<string, string>();

        errors.forEach((error: string) => {
          // Parse error messages like "Feed IKypDRhu2jqB1uc2: Failed to fetch feed: HTTP 404"
          const match = error.match(/Feed ([^:]+): (.+)/);
          if (match) {
            brokenMap.set(match[1], match[2]);
          }
        });

        setBrokenFeeds(brokenMap);
      }
    } catch (error) {
      console.error('Failed to check broken feeds:', error);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    setMessage(null);
    await loadFeeds();
    setRefreshing(false);
  };

  const handleIngestFeed = async (feedId: string) => {
    setIngestingFeeds(prev => new Set(prev).add(feedId));
    setMessage(null);

    try {
      const result = await api.triggerRSSAppIngestion(feedId);
      setMessage({
        type: 'success',
        text: `Successfully ingested ${result.processedCount || 0} items from feed`
      });

      // Refresh feeds after a delay
      setTimeout(loadFeeds, 2000);
    } catch (error) {
      console.error('Failed to ingest feed:', error);
      setMessage({ type: 'error', text: 'Failed to ingest feed' });
    } finally {
      setIngestingFeeds(prev => {
        const newSet = new Set(prev);
        newSet.delete(feedId);
        return newSet;
      });
    }
  };

  const handleIngestAll = async () => {
    setIngestingAll(true);
    setMessage(null);

    try {
      await api.triggerRSSAppIngestion();
      setMessage({ type: 'success', text: 'All RSS.app feeds ingestion started' });

      // Refresh feeds after a delay
      setTimeout(loadFeeds, 5000);
    } catch (error) {
      console.error('Failed to ingest all feeds:', error);
      setMessage({ type: 'error', text: 'Failed to start ingestion for all feeds' });
    } finally {
      setIngestingAll(false);
    }
  };

  const handleSelectFeed = (feedId: string) => {
    setSelectedFeeds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(feedId)) {
        newSet.delete(feedId);
      } else {
        newSet.add(feedId);
      }
      return newSet;
    });
  };

  const handleSelectAll = (feedsToSelect: RSSAppFeed[]) => {
    if (selectedFeeds.size === feedsToSelect.length) {
      setSelectedFeeds(new Set());
    } else {
      setSelectedFeeds(new Set(feedsToSelect.map(f => f.id)));
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedFeeds.size === 0) return;

    if (!confirm(`Are you sure you want to delete ${selectedFeeds.size} feed(s)? This action cannot be undone.`)) {
      return;
    }

    setDeletingFeeds(true);
    setMessage(null);

    try {
      // Call API to delete feeds
      await api.deleteRSSAppFeeds(Array.from(selectedFeeds));
      setMessage({
        type: 'success',
        text: `Successfully deleted ${selectedFeeds.size} feed(s)`
      });
      setSelectedFeeds(new Set());
      await loadFeeds();
    } catch (error) {
      console.error('Failed to delete feeds:', error);
      setMessage({ type: 'error', text: 'Failed to delete feeds' });
    } finally {
      setDeletingFeeds(false);
    }
  };

  const getFilteredFeeds = () => {
    switch (filter) {
      case 'healthy':
        return feeds.filter(f => f.status === 'healthy');
      case 'broken':
        return feeds.filter(f => f.status === 'broken');
      case 'inactive':
        return feeds.filter(f => f.status === 'inactive');
      default:
        return feeds;
    }
  };

  const filteredFeeds = getFilteredFeeds();
  const brokenCount = feeds.filter(f => f.status === 'broken').length;
  const inactiveCount = feeds.filter(f => f.status === 'inactive').length;
  const healthyCount = feeds.filter(f => f.status === 'healthy').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">RSS.app X Account Feeds</h1>
          <p className="text-gray-600 mt-1">
            Your configured X (Twitter) accounts from RSS.app. Feeds are automatically ingested every 15 minutes.
          </p>
          <p className="text-sm text-blue-600 mt-1">
            📊 For posts from all sources (including Bleeping Computer, etc.), visit the <Link to="/posts" className="underline hover:text-blue-800">Posts page</Link>
          </p>
        </div>
        <div className="flex space-x-4">
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={handleIngestAll}
            disabled={ingestingAll}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {ingestingAll ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Ingesting All...
              </>
            ) : (
              <>
                <Rss className="h-4 w-4 mr-2" />
                Ingest All Feeds
              </>
            )}
          </button>
        </div>
      </div>

      {/* Status Summary */}
      {feeds.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-gray-400">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Feeds</p>
                <p className="text-2xl font-bold text-gray-900">{feeds.length}</p>
              </div>
              <Rss className="h-8 w-8 text-gray-400" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-green-400">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Healthy</p>
                <p className="text-2xl font-bold text-green-600">{healthyCount}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-400" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-red-400">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Broken</p>
                <p className="text-2xl font-bold text-red-600">{brokenCount}</p>
              </div>
              <XCircle className="h-8 w-8 text-red-400" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-yellow-400">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Inactive</p>
                <p className="text-2xl font-bold text-yellow-600">{inactiveCount}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-yellow-400" />
            </div>
          </div>
        </div>
      )}

      {/* Filter and Actions Bar */}
      {feeds.length > 0 && (
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Filter className="h-5 w-5 text-gray-400" />
              <div className="flex space-x-2">
                <button
                  onClick={() => setFilter('all')}
                  className={`px-3 py-1 rounded-md text-sm font-medium ${filter === 'all'
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-600 hover:bg-gray-100'
                    }`}
                >
                  All ({feeds.length})
                </button>
                <button
                  onClick={() => setFilter('healthy')}
                  className={`px-3 py-1 rounded-md text-sm font-medium ${filter === 'healthy'
                      ? 'bg-green-100 text-green-700'
                      : 'text-gray-600 hover:bg-gray-100'
                    }`}
                >
                  Healthy ({healthyCount})
                </button>
                <button
                  onClick={() => setFilter('broken')}
                  className={`px-3 py-1 rounded-md text-sm font-medium ${filter === 'broken'
                      ? 'bg-red-100 text-red-700'
                      : 'text-gray-600 hover:bg-gray-100'
                    }`}
                >
                  Broken ({brokenCount})
                </button>
                <button
                  onClick={() => setFilter('inactive')}
                  className={`px-3 py-1 rounded-md text-sm font-medium ${filter === 'inactive'
                      ? 'bg-yellow-100 text-yellow-700'
                      : 'text-gray-600 hover:bg-gray-100'
                    }`}
                >
                  Inactive ({inactiveCount})
                </button>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              {selectedFeeds.size > 0 && (
                <>
                  <span className="text-sm text-gray-600">
                    {selectedFeeds.size} selected
                  </span>
                  <button
                    onClick={handleDeleteSelected}
                    disabled={deletingFeeds}
                    className="inline-flex items-center px-3 py-2 border border-red-300 rounded-md shadow-sm text-sm font-medium text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                  >
                    {deletingFeeds ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Deleting...
                      </>
                    ) : (
                      <>
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete Selected
                      </>
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Message */}
      {message && (
        <div className={`rounded-md p-4 ${message.type === 'success' ? 'bg-green-50' : 'bg-red-50'
          }`}>
          <div className="flex">
            <div className="flex-shrink-0">
              {message.type === 'success' ? (
                <CheckCircle className="h-5 w-5 text-green-400" />
              ) : (
                <AlertCircle className="h-5 w-5 text-red-400" />
              )}
            </div>
            <div className="ml-3">
              <p className={`text-sm font-medium ${message.type === 'success' ? 'text-green-800' : 'text-red-800'
                }`}>
                {message.text}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Feeds List */}
      {console.log('[DEBUG] 🖼️ Rendering. feeds:', feeds)}
      {(feeds === undefined || feeds === null) ? (
        <div className="bg-white shadow rounded-lg p-8">
          <div className="text-center">
            <Loader2 className="mx-auto h-12 w-12 text-blue-400 animate-spin" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Initializing feeds...</h3>
          </div>
        </div>
      ) : feeds.length === 0 ? (
        <div className="bg-white shadow rounded-lg p-8">
          <div className="text-center">
            {configurationRequired ? (
              <>
                <AlertCircle className="mx-auto h-12 w-12 text-amber-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">RSS.app API Configuration Required</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {configurationError || 'RSS.app API credentials need to be configured to display feeds.'}
                </p>
                <p className="mt-2 text-xs text-gray-400">
                  Please configure RSS_API_KEY and RSS_API_SECRET environment variables.
                </p>
              </>
            ) : (
              <>
                <Rss className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No RSS.app feeds configured</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Configure your X accounts in RSS.app to start tracking them.
                </p>
              </>
            )}
          </div>
        </div>
      ) : filteredFeeds.length === 0 ? (
        <div className="bg-white shadow rounded-lg p-8">
          <div className="text-center">
            <Filter className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No feeds match this filter</h3>
            <p className="mt-1 text-sm text-gray-500">
              Try selecting a different filter to view other feeds.
            </p>
          </div>
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={selectedFeeds.size === filteredFeeds.length && filteredFeeds.length > 0}
                onChange={() => handleSelectAll(filteredFeeds)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Select All</span>
            </label>
          </div>
          <ul className="divide-y divide-gray-200">
            {console.log('[DEBUG] 🚀 Executing feeds.map with length:', filteredFeeds.length)}
            {filteredFeeds.map((feed) => (
              <li key={feed.id} className={`px-6 py-4 ${feed.status === 'broken' ? 'bg-red-50' :
                  feed.status === 'inactive' ? 'bg-yellow-50' : ''
                }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center flex-1 min-w-0">
                    <input
                      type="checkbox"
                      checked={selectedFeeds.has(feed.id)}
                      onChange={() => handleSelectFeed(feed.id)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-4"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center">
                        <Twitter className="h-5 w-5 text-gray-400 mr-2" />
                        <h3 className="text-lg font-medium text-gray-900 truncate">
                          {feed.title}
                        </h3>
                        {feed.status === 'broken' && (
                          <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            <XCircle className="h-3 w-3 mr-1" />
                            Broken
                          </span>
                        )}
                        {feed.status === 'inactive' && (
                          <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            <AlertTriangle className="h-3 w-3 mr-1" />
                            Inactive
                          </span>
                        )}
                        {feed.status === 'healthy' && (
                          <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Healthy
                          </span>
                        )}
                      </div>
                      {feed.error && (
                        <p className="mt-1 text-sm text-red-600">
                          Error: {feed.error}
                        </p>
                      )}
                      {feed.description && (
                        <p className="mt-1 text-sm text-gray-500 line-clamp-2">
                          {feed.description}
                        </p>
                      )}
                      <div className="mt-2 flex items-center text-sm text-gray-500 space-x-4">
                        <span className="flex items-center">
                          <FileText className="h-4 w-4 mr-1" />
                          {feed.items_count} items
                        </span>
                        <span className="flex items-center">
                          <Calendar className="h-4 w-4 mr-1" />
                          {feed.last_post_date ? (
                            <>Last post: {new Date(feed.last_post_date).toLocaleString(undefined, {
                              year: 'numeric',
                              month: 'numeric',
                              day: 'numeric',
                              hour: 'numeric',
                              minute: '2-digit',
                              hour12: true
                            })}</>
                          ) : (
                            <span className="text-orange-600 font-medium">No posts</span>
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="ml-4 flex-shrink-0">
                    <button
                      onClick={() => handleIngestFeed(feed.id)}
                      disabled={ingestingFeeds.has(feed.id)}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                    >
                      {ingestingFeeds.has(feed.id) ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Ingesting...
                        </>
                      ) : (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2" />
                          Ingest Now
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <Rss className="h-5 w-5 text-blue-400" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">
              Automatic Ingestion Active
            </h3>
            <div className="mt-2 text-sm text-blue-700">
              <p>Your RSS.app feeds are automatically ingested every 15 minutes. Use the manual ingest buttons to pull updates immediately.</p>
              <p className="mt-2">Broken feeds are automatically detected and marked. You can filter and delete them using the controls above.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}